import { Component } from 'react';
import { hardReload } from './ThemeToggle';

// Catches render crashes — most importantly the version-skew "blank page":
// after a new deploy, a still-running/cached client lazy-loads an old
// main.<hash> chunk the new production deployment no longer serves, so the
// dynamic import() rejects and React unmounts to a white screen. We detect
// that class of error and hard-reload once (clears SW caches + cache-busts),
// which pulls the fresh index.html + current chunks. Any other crash shows a
// minimal recover UI instead of a blank tab.

const CHUNK_ERR = /loading chunk|loading css chunk|failed to fetch dynamically imported|importing a module script failed|dynamically imported module|chunkloaderror/i;
const RELOAD_TS_KEY = '3c_chunk_reload_ts';

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false };
  }

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(error) {
    const msg = (error && (error.message || error.name || String(error))) || '';
    // Surface the real error in the console for debugging (not shown to staff).
    try { console.error('[ErrorBoundary]', error); } catch (_) {}
    const isChunk = CHUNK_ERR.test(msg) || (error && error.name === 'ChunkLoadError');
    if (!isChunk) return;
    // Reload at most once per 10s so a genuinely broken build can't loop.
    let last = 0;
    try { last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || 0); } catch (_) {}
    const now = Date.now();
    if (now - last > 10000) {
      try { sessionStorage.setItem(RELOAD_TS_KEY, String(now)); } catch (_) {}
      hardReload();
    }
  }

  render() {
    if (this.state.crashed) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24,
          fontFamily: 'Inter, system-ui, sans-serif', textAlign: 'center',
          background: 'var(--bg, #000)', color: 'var(--text, #fff)',
        }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Something went wrong loading the app.</div>
          <div style={{ fontSize: 13, opacity: 0.7, maxWidth: 360 }}>
            This usually clears with a refresh. If it keeps happening, fully close and reopen the app.
          </div>
          <button
            onClick={() => hardReload()}
            style={{
              padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              borderRadius: 8, border: 'none', background: 'var(--blue, #2563eb)', color: '#fff',
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
